import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import TopBar from '../components/layout/TopBar';
import InvoiceQueueTable from '../components/queue/InvoiceQueueTable';
import QueueToolbar from '../components/queue/QueueToolbar';
import MergeDialog from '../components/queue/MergeDialog';
import HistoryTable from '../components/queue/HistoryTable';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { fetchInvoices, updateInvoice, rejectInvoice, batchReject, approveInvoice, batchApprove, mergeInvoices, getBillingRate, applyDiscount } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import DiscountModal from '../components/queue/DiscountModal';

export default function DashboardPage() {
  const { session } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [billingRate, setBillingRate] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [discountInvoice, setDiscountInvoice] = useState(null);

  const loadBillingRate = useCallback(async () => {
    try {
      const res = await getBillingRate();
      setBillingRate(res.data.rate);
    } catch (err) {
      console.warn('[Dashboard] Failed to fetch billing rate, using default');
      setBillingRate(195);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchInvoices('pending');
      setInvoices(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load invoices');
      console.error('[Dashboard] Load error:', err.response?.status, err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (session?.access_token) {
      loadBillingRate();
      loadInvoices();
    }
  }, [session, loadInvoices, loadBillingRate]);

  const displayInvoices = invoices.map(inv => {
    if (billingRate != null) {
      const hours = parseFloat(inv.hours) || 0;
      return { ...inv, display_amount: hours * billingRate };
    }
    return { ...inv, display_amount: parseFloat(inv.amount) || 0 };
  });

  const handleDescriptionUpdate = async (id, newDescription) => {
    try {
      await updateInvoice(id, { description: newDescription });
      setInvoices(prev => prev.map(inv =>
        inv.id === id ? { ...inv, description: newDescription } : inv
      ));
      toast.success('Description updated');
    } catch (err) {
      toast.error('Failed to update description');
      throw err;
    }
  };

  const handleEmailUpdate = async (id, newEmail) => {
    try {
      await updateInvoice(id, { client_email: newEmail });
      setInvoices(prev => prev.map(inv =>
        inv.id === id ? { ...inv, client_email: newEmail } : inv
      ));
      toast.success('Email updated');
    } catch (err) {
      toast.error('Failed to update email');
      throw err;
    }
  };

  const handleHoursUpdate = async (id, newHours) => {
    try {
      const hours = parseFloat(newHours) || 0;
      await updateInvoice(id, { hours });
      setInvoices(prev => prev.map(inv =>
        inv.id === id ? { ...inv, hours } : inv
      ));
      toast.success('Hours updated');
    } catch (err) {
      toast.error('Failed to update hours');
      throw err;
    }
  };

  const handleApplyDiscount = async (id, type, value, reason) => {
    try {
      const res = await applyDiscount(id, type, value, reason);
      setInvoices(prev => prev.map(inv => inv.id === id ? res.data.data : inv));
      if (type === 'none') {
        toast.success('Discount cleared');
      } else {
        toast.success('Discount applied');
      }
    } catch (err) {
      toast.error('Failed to apply discount');
      throw err;
    }
  };

  const handleReject = async () => {
    if (selectedIds.length === 0) return;
    setActionLoading('reject');
    try {
      if (selectedIds.length === 1) {
        await rejectInvoice(selectedIds[0]);
      } else {
        await batchReject(selectedIds);
      }
      setInvoices(prev => prev.filter(inv => !selectedIds.includes(inv.id)));
      toast.success(`Rejected ${selectedIds.length} item${selectedIds.length > 1 ? 's' : ''}`);
      setSelectedIds([]);
    } catch (err) {
      toast.error('Failed to reject items');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async () => {
    if (selectedIds.length === 0) return;
    setActionLoading('approve');
    try {
      if (selectedIds.length === 1) {
        await approveInvoice(selectedIds[0]);
      } else {
        await batchApprove(selectedIds);
      }
      setInvoices(prev => prev.filter(inv => !selectedIds.includes(inv.id)));
      toast.success(`Approved ${selectedIds.length} item${selectedIds.length > 1 ? 's' : ''} — invoice sent via GoHighLevel`);
      setSelectedIds([]);
    } catch (err) {
      const raw = err.response?.data?.detail || 'Failed to approve items';
      const parts = raw.split('|');
      const message = parts[0];
      const crmLink = parts[1] || null;
      toast.error(message, {
        duration: 12000,
        action: crmLink ? {
          label: 'Open CRM Contacts',
          onClick: () => window.open(crmLink, '_blank'),
        } : undefined,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleMerge = async () => {
    if (selectedIds.length < 2) {
      toast.error('Select at least 2 items to merge');
      return;
    }
    setMergeDialogOpen(true);
  };

  const handleMergeConfirm = async (overrideEmail) => {
    setActionLoading('merge');
    setMergeDialogOpen(false);
    try {
      const res = await mergeInvoices(selectedIds, overrideEmail);
      setInvoices(prev => {
        const filtered = prev.filter(inv => !selectedIds.includes(inv.id));
        return [res.data.data, ...filtered];
      });
      toast.success(`Merged ${selectedIds.length} items into 1`);
      setSelectedIds([]);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to merge items';
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? invoices.map(inv => inv.id) : []);
  };

  const handleSelectOne = (id, checked) => {
    setSelectedIds(prev => checked ? [...prev, id] : prev.filter(i => i !== id));
  };

  const selectedInvoices = displayInvoices.filter(inv => selectedIds.includes(inv.id));
  const totalSelectedAmount = selectedInvoices.reduce((sum, inv) => sum + (inv.display_amount || 0), 0);
  const totalSelectedHours = selectedInvoices.reduce((sum, inv) => sum + (parseFloat(inv.hours) || 0), 0);

  const handleRefresh = () => {
    loadBillingRate();
    loadInvoices();
  };

  // When an item is restored from history, reload the pending queue
  const handleRestored = () => {
    loadInvoices();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <TopBar onRefresh={handleRefresh} billingRate={billingRate} />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-[340px] grid-cols-2">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending Queue
              {displayInvoices.length > 0 && (
                <span className="ml-1.5 text-[10px] bg-primary/10 text-primary rounded-full px-1.5 py-0.5 font-semibold">
                  {displayInvoices.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Action History</TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card className="shadow-[var(--shadow-1)]">
              <CardHeader className="pb-0 pt-4 px-4 sm:px-6">
                <QueueToolbar
                  selectedCount={selectedIds.length}
                  totalAmount={totalSelectedAmount}
                  totalHours={totalSelectedHours}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onMerge={handleMerge}
                  actionLoading={actionLoading}
                  totalItems={displayInvoices.length}
                />
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <InvoiceQueueTable
                  invoices={displayInvoices}
                  selectedIds={selectedIds}
                  onSelectAll={handleSelectAll}
                  onSelectOne={handleSelectOne}
                  onDescriptionUpdate={handleDescriptionUpdate}
                  onEmailUpdate={handleEmailUpdate}
                  onHoursUpdate={handleHoursUpdate}
                  onDiscountClick={(inv) => setDiscountInvoice(inv)}
                  loading={loading}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card className="shadow-[var(--shadow-1)]">
              <CardContent className="px-0 pt-4 pb-0">
                <HistoryTable onRestored={handleRestored} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <MergeDialog
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        onConfirm={handleMergeConfirm}
        selectedInvoices={selectedInvoices}
        totalAmount={totalSelectedAmount}
        totalHours={totalSelectedHours}
        loading={actionLoading === 'merge'}
      />

      <DiscountModal
        open={!!discountInvoice}
        onOpenChange={(o) => { if (!o) setDiscountInvoice(null); }}
        invoice={discountInvoice}
        onSave={handleApplyDiscount}
      />
    </div>
  );
}
